import { warehouseApi } from 'src/api/warehouse';
import {
  WarehouseBatchDto,
  WarehouseProductDetailDto,
  WarehouseProductDto,
} from 'woofwoof-api';
import { ClinicalProductKind } from '../utils/clinicalStock';
import { warehouseRequestOptions } from '../utils/request';
import {
  canWriteStock,
  useWarehouseActivation,
} from './useWarehouseActivation';
import { useWarehouseProduct } from './useWarehouseProduct';
import {
  EMPTY_PRODUCT_FILTERS,
  PRODUCT_PICKER_LIMIT,
  buildProductListParams,
} from './useWarehouseProducts';
import { useWarehouseQuery } from './useWarehouseQuery';

export type { ClinicalProductKind } from '../utils/clinicalStock';

const NO_PRODUCTS: WarehouseProductDto[] = [];
const NO_BATCHES: WarehouseBatchDto[] = [];

/**
 * The only place clinical forms search Phase 1 Products: one kind, active
 * only, a first page. Key `clinicalPickerKey(organizationId, kind, search)`;
 * the organization is in the key and no previous data is kept, so a clinic
 * switch never offers the previous clinic's Products (review S3).
 */
export function useStockProductOptions(
  kind: ClinicalProductKind,
  search: string,
  enabled: boolean,
): { options: WarehouseProductDto[]; isLoading: boolean } {
  const term = search.trim();
  const params = buildProductListParams(
    { ...EMPTY_PRODUCT_FILTERS, kind, search: term },
    0,
    PRODUCT_PICKER_LIMIT,
  );
  const query = useWarehouseQuery(
    'warehouse-products',
    ['clinical-picker', kind, term],
    (organizationId, signal) =>
      warehouseApi
        .warehouseProductControllerList(
          params,
          warehouseRequestOptions(organizationId, signal),
        )
        .then((response) => response.data),
    { enabled },
  );
  return {
    options: query.data?.data ?? NO_PRODUCTS,
    isLoading: enabled && query.isInitialLoading,
  };
}

/**
 * Whether this clinic may take stock now (revision R1). Unknown counts as
 * disabled, so the stock section never flashes on before the answer.
 */
export function useWarehouseStockEnabled(): boolean {
  // Phase 1's hook (key ['warehouse-activation', organizationId]) and rule.
  return canWriteStock(useWarehouseActivation().data);
}

/**
 * Phase 1's detail query, so the form and the Magazyn page share one cache
 * entry (`['warehouse-product', organizationId, productId]`). An empty id
 * asks for nothing.
 */
export function useStockProduct(
  productId: string,
): WarehouseProductDetailDto | undefined {
  return useWarehouseProduct(productId).data;
}

/**
 * The Product's detail is on its first read: the stock step shows skeletons
 * where its stock and batches will be. An empty id reads nothing.
 */
export function useStockProductLoading(productId: string): boolean {
  return useWarehouseProduct(productId).isInitialLoading;
}

/** The detail already carries the Batches; no second request. */
export function useStockBatches(productId: string): WarehouseBatchDto[] {
  return useWarehouseProduct(productId).data?.batches ?? NO_BATCHES;
}
